import React, { useState } from "react";
import { toast } from "sonner";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { FileUpload } from "@/components/FileUpload";
import { NicknameField } from "@/components/wallet/NicknameField";

const inputCls = "w-full rounded-xl bg-[#0d0d0d] border border-white/10 px-4 py-3 text-sm focus:border-[#ccff00]/50 focus:outline-none";
const labelCls = "block text-xs font-mono-cl tracking-[0.14em] uppercase text-zinc-500 mb-1.5";

function PrivacyToggle({ k, label, hint, value, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-white/5">
      <div><p className="text-sm font-medium">{label}</p><p className="text-xs text-zinc-500 mt-0.5">{hint}</p></div>
      <Switch data-testid={`privacy-${k}`} checked={value} onCheckedChange={(v) => onChange(k, v)} />
    </div>
  );
}

export function ProfileSection() {
  const { user, setUser } = useAuth();
  const p = user.privacy || {};
  const [f, setF] = useState({ display_name: user.name || "", bio: user.bio || "", picture: user.picture || "" });
  const [priv, setPriv] = useState({ public_profile: p.public_profile !== false, show_subscriptions: !!p.show_subscriptions, show_wallet: !!p.show_wallet });
  const [nick, setNick] = useState(user.nickname || "");
  const [nickValid, setNickValid] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try { setUser((await client.patch("/account/profile", { ...f, ...priv })).data.user); toast.success("Profile saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const saveNick = async () => {
    try { setUser((await client.post("/account/nickname", { nickname: nick })).data.user); toast.success("Nickname updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="grid gap-10 lg:grid-cols-[1fr_320px]">
      <div className="space-y-5">
        <div>
          <label className={labelCls}>Nickname</label>
          <NicknameField value={nick} onChange={setNick} onValidity={setNickValid} testid="profile-nickname" />
          {nick !== (user.nickname || "") && (
            <button data-testid="profile-nickname-save" disabled={!nickValid} onClick={saveNick}
              className="mt-2 px-5 py-2 rounded-full border border-[#ccff00]/50 text-[#ccff00] text-xs font-semibold disabled:opacity-40">Save nickname</button>
          )}
          <p className="text-[11px] text-zinc-600 mt-1">Nicknames can be changed once every 30 days.</p>
        </div>
        <div><label className={labelCls}>Display name</label>
          <input data-testid="profile-display-name" className={inputCls} maxLength={60} value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} /></div>
        <div><label className={labelCls}>Bio</label>
          <textarea data-testid="profile-bio" className={inputCls + " min-h-[96px]"} maxLength={280} value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} />
          <p className="text-[11px] text-zinc-600 text-right">{f.bio.length}/280</p></div>
        <div><label className={labelCls}>Avatar</label>
          <FileUpload value={f.picture} onChange={(v) => setF({ ...f, picture: v })} aspect="1/1" previewClass="w-24" testid="profile-avatar" /></div>
      </div>
      <div>
        <p className={labelCls}>Privacy</p>
        <PrivacyToggle k="public_profile" label="Public profile" hint="Anyone can view your nickname, avatar and bio." value={priv.public_profile} onChange={(k, v) => setPriv({ ...priv, [k]: v })} />
        <PrivacyToggle k="show_subscriptions" label="Show analysts I follow" hint="Lists your active analyst subscriptions on your profile." value={priv.show_subscriptions} onChange={(k, v) => setPriv({ ...priv, [k]: v })} />
        <PrivacyToggle k="show_wallet" label="Show primary wallet" hint="Displays a shortened address only." value={priv.show_wallet} onChange={(k, v) => setPriv({ ...priv, [k]: v })} />
        <p className="text-xs text-zinc-500 mt-4">Your email, payment history and full wallet addresses are never shown publicly.</p>
        <button data-testid="profile-save" disabled={busy} onClick={save}
          className="mt-6 w-full py-3 rounded-full bg-[#ccff00] text-black font-semibold text-sm hover:brightness-110 transition-[filter] disabled:opacity-60">{busy ? "Saving…" : "Save profile"}</button>
      </div>
    </div>
  );
}
